import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { forumApi } from "../../utils/forumApi";
import ReplyForm from "./ReplyForm";
import ReplyItem from "./ReplyItem";
import {
  ContentGrid,
  EmptyState,
  EmptyText,
  EmptyTitle,
  ForumSection,
  ForumShell,
  HeroActions,
  HeroCard,
  HeroEyebrow,
  HeroText,
  MainColumn,
  MetaPill,
  MetaRow,
  Panel,
  PanelText,
  PanelTitle,
  SecondaryButton,
  Sidebar,
  Card,
  CardExcerpt,
  CardTitle,
} from "./ForumStyles";

function QuestionDetailPage() {
  const { slug } = useParams();
  const [question, setQuestion] = useState(null);
  const [replies, setReplies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadQuestion = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await forumApi.getQuestion(slug);
        if (!isMounted) return;
        setQuestion(response.question || null);
        setReplies(response.replies || []);
      } catch (fetchError) {
        if (!isMounted) return;
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : "Could not load this question."
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    if (slug) {
      loadQuestion();
    }

    return () => {
      isMounted = false;
    };
  }, [slug]);

  return (
    <ForumSection>
      <ForumShell>
        <HeroCard>
          <HeroEyebrow>Forum Discussion</HeroEyebrow>
          <HeroText>
            Read the discussion below and share a helpful reply if you have relevant
            technical insight. Replies go through moderation before they appear.
          </HeroText>
          <HeroActions>
            <SecondaryButton to="/forum">Back to Forum</SecondaryButton>
          </HeroActions>
        </HeroCard>

        <ContentGrid>
          <MainColumn>
            {isLoading ? (
              <EmptyState>
                <EmptyTitle>Loading discussion...</EmptyTitle>
                {/* <EmptyText>Please wait while we fetch the question details.</EmptyText> */}
              </EmptyState>
            ) : null}

            {!isLoading && error ? (
              <EmptyState>
                <EmptyTitle>Question unavailable</EmptyTitle>
                <EmptyText>{error}</EmptyText>
              </EmptyState>
            ) : null}

            {!isLoading && !error && question ? (
              <>
                <Card>
                  <MetaRow>
                    <MetaPill $accent>{question.category}</MetaPill>
                    {/* <MetaPill>{question.viewCount || 0} views</MetaPill> */}
                    <MetaPill>{question.replyCount || 0} replies</MetaPill>
                    <MetaPill>Asked by {question.authorName || "Anonymous"}</MetaPill>
                  </MetaRow>
                  <CardTitle>{question.title}</CardTitle>
                  <CardExcerpt>{question.body}</CardExcerpt>
                </Card>

                <Panel>
                  <PanelTitle>Replies</PanelTitle>
                  {replies.length === 0 ? (
                    <PanelText>
                      There are no replies yet. You can be the first to add a
                      helpful answer.
                    </PanelText>
                  ) : (
                    replies.map((reply) => <ReplyItem key={reply.id} reply={reply} />)
                  )}
                </Panel>

                <ReplyForm questionId={question.id} />
              </>
            ) : null}
          </MainColumn>

          <Sidebar>
            <Panel>
              <PanelTitle>Reply tips</PanelTitle>
              <PanelText>
                Practical replies are best. If possible, explain the reasoning, relevant
                engineering standard, or the operational tradeoff behind your suggestion.
              </PanelText>
            </Panel>

            <Panel>
              <PanelTitle>Need training support?</PanelTitle>
              <PanelText>
                If your question connects to a broader training need, you can also explore
                our current engineering courses and schedules.
              </PanelText>
              <HeroActions>
                <SecondaryButton to="/training">View Training</SecondaryButton>
              </HeroActions>
            </Panel>
          </Sidebar>
        </ContentGrid>
      </ForumShell>
    </ForumSection>
  );
}

export default QuestionDetailPage;
